import { useMemo, useState } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  blankProgramSeed,
  coerceLocale,
  pickLocalizedText,
  programCloneBody,
  resolveProgramTemplate,
  type EffortScale,
  type Exercise,
  type NewProgram,
  type ProgramModel,
} from "@heraion/shared";

import { busyAria } from "../a11y";
import { difficultyLabel } from "../Library/difficultyLabel";
import { spokenLabel } from "../Library/LibraryItemCardActions";
import { useTemplates } from "../Library/useTemplates";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { Button } from "../primitives/Button";
import { IconButton } from "../primitives/IconButton";
import { SectionHeader } from "../primitives/SectionHeader";
import { writeError, type ConfirmFn, type DurableWriteFn } from "../seams";
import { resolveTheme, type UiTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";
import { ModelWizard } from "./ModelWizard";
import { makeSetupStyles } from "./setupStyles";

/** «From scratch» (§5.4b): one empty day and four rounds; the name is typed
 *  in the editor. */
const SCRATCH_ROUNDS = 4;

type ListKey = "models" | "templates";
type Stage = { kind: "start" } | { kind: ListKey } | { kind: "wizard"; model: Omit<ProgramModel, "id">; from: ListKey };

type EntryState = { disabled?: boolean; busy?: boolean };

// «New program» (#336 slice 5, #351, brief §5.4b): from a model (its
// wizard), from a public template (the same copy the Library makes), or from
// scratch. Models and templates are rows in the DB, read once on mount; an
// entry with nothing to offer stays, and says why. #354: one «From a model»
// list, two sections: Built-in, and the caller's own (GET ?scope=mine) under
// «My models», with a delete each. `initialModel` opens that model's wizard.
export function NewProgramStart({
  dark,
  apiFetch,
  catalog,
  effortScale,
  confirm,
  durableWrite,
  initialModel,
  onCreate,
  onCancel,
}: {
  dark: boolean;
  apiFetch: ProgramApiFetch;
  catalog: ReadonlyArray<Exercise> | undefined;
  effortScale: EffortScale;
  /** The host's confirm and write; without both, a model of one's own has
   *  no delete. */
  confirm?: ConfirmFn;
  durableWrite?: DurableWriteFn;
  /** A model chosen elsewhere (the Library): start on its wizard. */
  initialModel?: Omit<ProgramModel, "id">;
  onCreate: (seed: NewProgram) => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = coerceLocale(i18n.language);
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  const styles = useMemo(() => makeSetupStyles(theme), [theme]);
  const local = useMemo(() => makeStyles(theme), [theme]);
  const read = useTemplates({ apiFetch, kinds: ["program", "model", "mine"] });
  const [stage, setStage] = useState<Stage>(
    initialModel ? { kind: "wizard", model: initialModel, from: "models" } : { kind: "start" },
  );
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const toStart = () => setStage({ kind: "start" });
  const ours = read.models;
  const mine = read.myModels;

  if (stage.kind === "wizard") {
    return (
      <ModelWizard
        theme={theme}
        model={stage.model}
        catalog={catalog}
        effortScale={effortScale}
        onCreate={onCreate}
        onBack={() => setStage({ kind: stage.from })}
      />
    );
  }

  const removeModel = async (m: ProgramModel) => {
    if (!confirm || !durableWrite || deleting) return;
    const name = pickLocalizedText(m.name, lang);
    const ok = await confirm({
      title: t("editorV3.model.deleteTitle", { defaultValue: "Delete this model?" }),
      body: t("editorV3.model.deleteBody", {
        name,
        defaultValue: "\"{{name}}\" goes away. Programs made from it stay.",
      }),
      confirmLabel: t("workout.delete", { defaultValue: "Delete" }),
      cancelLabel: t("program.cancel", { defaultValue: "Cancel" }),
    });
    if (!ok) return;
    setDeleting(m.id);
    setDeleteFailed(false);
    let failed = true;
    try {
      const err = writeError(
        await durableWrite({
          method: "DELETE",
          path: `/api/templates/models/${encodeURIComponent(m.id)}?scope=mine`,
        }),
      );
      if (err) console.warn("[newProgram] delete_model_failed", err.message);
      else failed = false;
    } catch (e) {
      console.warn("[newProgram] delete_model_failed", e instanceof Error ? e.message : e);
    } finally {
      setDeleting(null);
      setDeleteFailed(failed);
    }
    if (!failed) read.reload();
  };

  const titleOf = (key: ListKey) =>
    key === "models"
      ? t("editorV3.model.fromModel", { defaultValue: "From a model" })
      : t("editorV3.model.fromTemplate", { defaultValue: "From a template" });
  const entry = (key: string, title: string, body: string, onPress: () => void, state: EntryState = {}) => (
    <Pressable
      key={key}
      accessibilityRole="button"
      accessibilityLabel={`${title}. ${body}`}
      accessibilityState={{ disabled: state.disabled === true, busy: state.busy === true }}
      // RNW drops accessibilityState: these reach the web.
      aria-disabled={state.disabled === true}
      {...busyAria(state.busy)}
      disabled={state.disabled}
      onPress={onPress}
      style={({ pressed }) => [local.entry, pressed && local.pressed]}
    >
      <Text style={state.disabled ? local.entryTitleOff : local.entryTitle}>{title}</Text>
      {/* The body is what changes loading, error, empty, ready: said aloud. */}
      <Text style={styles.hint} accessibilityLiveRegion="polite" aria-live="polite">
        {body}
      </Text>
    </Pressable>
  );
  const back = (
    <Button theme={theme} variant="ghost" label={t("editorV3.tetrad.back", { defaultValue: "Back" })} onPress={toStart} full />
  );

  const modelEntry = (m: ProgramModel, from: ListKey) => {
    // Only the row being deleted says so; the others stay as they are.
    const gone = deleting === m.id;
    return entry(
      m.id,
      pickLocalizedText(m.name, lang),
      gone ? t("editorV3.model.deleting", { defaultValue: "Deleting…" }) : pickLocalizedText(m.description, lang),
      () => setStage({ kind: "wizard", model: m, from }),
      gone ? { disabled: true, busy: true } : {},
    );
  };

  if (stage.kind === "models" || stage.kind === "templates") {
    return (
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title} accessibilityRole="header" role="heading">
          {titleOf(stage.kind)}
        </Text>
        {stage.kind === "models" ? (
          <>
            {ours.length > 0 ? (
              <>
                <SectionHeader theme={theme} title={t("editorV3.model.builtIn", { defaultValue: "Built-in" })} />
                {ours.map((m) => modelEntry(m, "models"))}
              </>
            ) : null}
            <SectionHeader theme={theme} title={t("editorV3.model.mine", { defaultValue: "My models" })} />
            {deleteFailed ? (
              <Text style={local.error} accessibilityLiveRegion="polite" aria-live="polite">
                {t("editorV3.model.deleteError", { defaultValue: "Could not delete the model. Try again." })}
              </Text>
            ) : null}
            {mine.length === 0 ? (
              <Text style={styles.hint}>
                {t("editorV3.model.noMine", {
                  defaultValue: "None yet. In the Library, tap \"Save as model\" on one of your programs.",
                })}
              </Text>
            ) : null}
            {mine.map((m) => (
              <View key={m.id} style={local.mineRow}>
                <View style={local.mineEntry}>{modelEntry(m, "models")}</View>
                {confirm && durableWrite ? (
                  <IconButton
                    theme={theme}
                    name="trash"
                    variant="destructive"
                    onPress={() => void removeModel(m)}
                    disabled={deleting === m.id}
                    accessibilityLabel={spokenLabel(
                      t("workout.delete", { defaultValue: "Delete" }),
                      pickLocalizedText(m.name, lang),
                    )}
                  />
                ) : null}
              </View>
            ))}
          </>
        ) : (
          read.programTemplates.map((tpl) => {
            const resolved = resolveProgramTemplate(tpl, lang);
            return entry(
              tpl.id,
              resolved.name,
              `${difficultyLabel(tpl.difficulty, t)} · ${resolved.description}`,
              // The Library's copy, as a draft the editor opens.
              () => onCreate(programCloneBody(resolved, { status: "draft" })),
            );
          })
        )}
        {back}
      </ScrollView>
    );
  }

  // One read for all lists: loading, error and empty speak in each entry.
  const listEntry = (key: ListKey, count: number, body: string, none: string) =>
    read.status === "loading"
      ? entry(key, titleOf(key), t("editorV3.model.loading", { defaultValue: "Loading…" }), () => {}, {
          disabled: true,
          busy: true,
        })
      : read.status === "error"
        ? entry(key, titleOf(key), t("editorV3.model.loadError", { defaultValue: "Could not load. Tap to try again." }), read.reload)
        : count === 0
          ? entry(key, titleOf(key), none, () => {}, { disabled: true })
          : entry(key, titleOf(key), body, () => setStage({ kind: key }));

  return (
    <View style={styles.container}>
      <Text style={styles.title} accessibilityRole="header" role="heading">
        {t("program.newTitle", { defaultValue: "New program" })}
      </Text>
      {listEntry(
        "models",
        ours.length + mine.length,
        t("editorV3.model.fromModelBody", {
          defaultValue: "The model writes the days and the rounds. You choose the level, the time per session and the exercises.",
        }),
        t("editorV3.model.noModel", { defaultValue: "No model yet." }),
      )}
      {listEntry(
        "templates",
        read.programTemplates.length,
        t("editorV3.model.fromTemplateBody", { defaultValue: "A copy of a public program, to change as you like." }),
        t("editorV3.model.noTemplate", { defaultValue: "No template yet." }),
      )}
      {entry(
        "scratch",
        t("editorV3.tetrad.scratchTitle", { defaultValue: "From scratch" }),
        t("editorV3.tetrad.scratchBody", { defaultValue: "One empty day and four rounds." }),
        () => onCreate(blankProgramSeed("", SCRATCH_ROUNDS, 1)),
      )}
      <Button theme={theme} variant="ghost" label={t("program.cancel", { defaultValue: "Cancel" })} onPress={onCancel} full />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    entry: {
      minHeight: 44,
      padding: s.s4,
      gap: s.s1,
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface,
    },
    pressed: { backgroundColor: p.surface2 },
    entryTitle: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "700" },
    // Nothing to open: quieter, and its line says why.
    entryTitleOff: { color: p.ink3, fontSize: ty.sizes.base, fontWeight: "700" },
    // A model of one's own: its entry, and the delete beside it.
    mineRow: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    mineEntry: { flex: 1 },
    error: { color: p.danger, fontSize: ty.sizes.sm },
  });
}
